import { watch, Ref, ref, reactive, WatchStopHandle } from 'vue';
import emitter, { EB_EVENT_NAME } from '@/utils/eventbus';
import { ITreasure, IAnimation } from '@/service/broadcast/types';
import { useStore } from 'vuex';

enum IAnimationType {
  lowValue = 1,
  highValue = 2,
  highPriority = 3,
}

export interface EffectQueueEle {
  animations: IAnimation[], // 动画
  // value: number, // 单独礼物价值
  // startTime: number, // startTime 进队时间
}

export interface EffectContext {
  getNextEffect: () => (EffectQueueEle | undefined),
}

// 当前用户mid
const mid: Ref<number> = ref(0);

// 连击动画最大容量
const maxLowValueCapacity: number = 10;
const maxHighValueCapacity: number = 20;
const maxHighPriorityCapacity: number = Number.MAX_VALUE;
const maxOwnCapacity: number = Number.MAX_VALUE;


// 如果需要私有化，可以将所有队列都收敛进独立作用域内
const lowValueBufferEffects: EffectQueueEle[] = reactive(new Array<EffectQueueEle>(0));
const highValueBufferEffects: EffectQueueEle[] = reactive(new Array<EffectQueueEle>(0));
const highPriorityBufferEffects: EffectQueueEle[] = reactive(new Array<EffectQueueEle>(0));
const ownBufferEffects: EffectQueueEle[] = reactive(new Array<EffectQueueEle>(0));

const sentGifts: Map<string, boolean> = new Map<string, boolean>();


const resolveEffect = async (params: { data: ITreasure[], from: string }): Promise<void> => {
  // 不是函数式编程，后续可以优化
  for (let i = 0; i < params.data.length; i++) {
    const treasureData: ITreasure = params.data[i];
    const isOwn = treasureData.author.mid === mid.value;
    // const isFromApi = params.from === 'api';
   

    if (
      // 主态，假如已经被收录
      (isOwn && sentGifts.has(treasureData.msg_id))
        // 不满足最低价值要求
        || treasureData.animation_type < IAnimationType.lowValue
        // 无动画
        || !treasureData.animation_list?.length
    ) {
      continue;
    }
    if (isOwn) {
      sentGifts.set(treasureData.msg_id, true);
    }
    let targetBufferQueue, capacity;
   
    if (isOwn) {
      switch (treasureData.animation_type) {
        // 主态高优
        case IAnimationType.highPriority: {
          targetBufferQueue = highPriorityBufferEffects;
          capacity = maxHighPriorityCapacity;
          break;
        }
        // 主态来源
        default: {
          targetBufferQueue = ownBufferEffects;
          capacity = maxOwnCapacity;
          break;
        }
      }
    } else {
      switch (treasureData.animation_type) {
        // 客态一般价值
        case IAnimationType.lowValue: {
          targetBufferQueue = lowValueBufferEffects;
          capacity = maxLowValueCapacity;
          break;
        }
        // 客态高价值
        case IAnimationType.highValue: {
          targetBufferQueue = highValueBufferEffects;
          capacity = maxHighValueCapacity;
          break;
        }
      }
    }
    if (!targetBufferQueue || !capacity) {
      continue;
    }
    try {
      insertEffect(
        targetBufferQueue,
        capacity,
        treasureData,
      );
    } catch (e) {
      continue;
    }
  }
  // console.log({
  //   high: highValueBufferEffects.length,
  //   low: lowValueBufferEffects.length,
  //   own: ownBufferEffects.length
  // });
  // sortEffects(ownBufferEffects);
  // sortEffects(highValueBufferEffects);
  // sortEffects(lowValueBufferEffects);
};

const clearEffect = () => {
  lowValueBufferEffects.splice(0, lowValueBufferEffects.length);
  highValueBufferEffects.splice(0, highValueBufferEffects.length);
  highPriorityBufferEffects.splice(0, highPriorityBufferEffects.length);
  ownBufferEffects.splice(0, ownBufferEffects.length);
};

emitter.on(EB_EVENT_NAME.EFFECT_RECEIVED, resolveEffect);
emitter.on(EB_EVENT_NAME.LIVE_REFRESH, clearEffect);

// 插入排序位置，并删除上一条同Effect-id记录
const insertEffect = (queue: EffectQueueEle[], capacity: number, treasure: ITreasure) => {
  if (queue.length >= capacity) {
    return;
  }

  const newQueueEle = {
    animations: treasure.animation_list,
  } as EffectQueueEle;
  queue.push(newQueueEle);
  emitter.emit(EB_EVENT_NAME.EFFECT_START);
};

// // 队列排序，时间
// const sortEffects = (queue: EffectQueueEle[]): void => {
//   queue.sort((a, b) => (a.startTime - b.startTime));
// };


export default function useEffect(): EffectContext {
  const store = useStore();

  const userInfo = store.state?.global?.userInfo;
  if (userInfo) {
    mid.value = parseInt(userInfo.mid, 10);
  } else {
    const stopWatchMid: WatchStopHandle = watch(store.state.global, (newState: any) => {
      if (newState.userInfo) {
        mid.value = parseInt(newState.userInfo?.mid, 10);
        stopWatchMid();
      }
    });
  }

  const getNextEffect = (): EffectQueueEle | undefined => {
    if (highPriorityBufferEffects.length) {
      return highPriorityBufferEffects.shift();
    }
    if (ownBufferEffects.length) {
      return ownBufferEffects.shift();
    }
    if (highValueBufferEffects.length) {
      return highValueBufferEffects.shift();
    }
    if (lowValueBufferEffects.length) {
      return lowValueBufferEffects.shift();
    }
  };

  return {
    getNextEffect,
  };
};
